import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { api, CATEGORY_COLORS } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Link as LinkIcon } from "lucide-react";
import { compressImage } from "@/lib/imageCompress";

const MAX_IMG_BYTES = 15 * 1024 * 1024; // 15MB raw file, will be compressed
const COLORS = ["blue", "emerald", "rose", "amber", "violet", "slate"];

export default function OrganizationDialog({ open, onOpenChange, editing, onDone }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [color, setColor] = useState("slate");
    const [notes, setNotes] = useState("");
    const [images, setImages] = useState([]);
    const [imageUrlInput, setImageUrlInput] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setName(editing.name);
            setDescription(editing.description || "");
            setColor(editing.color || "slate");
            setNotes(editing.notes || "");
            setImages(
                (editing.images || []).map((i) =>
                    typeof i === "string" ? { url: i, caption: "" } : i
                )
            );
        } else {
            setName("");
            setDescription("");
            setColor("slate");
            setNotes("");
            setImages([]);
        }
    }, [open, editing]);

    const onAddImages = async (e) => {
        const files = Array.from(e.target.files || []);
        for (const f of files) {
            if (f.size > MAX_IMG_BYTES) {
                toast.error(`${f.name} trop lourde (max 15 MB)`);
                continue;
            }
            try {
                const url = await compressImage(f, { maxDim: 1200, quality: 0.82 });
                setImages((prev) => [...prev, { url, caption: "" }]);
            } catch {
                toast.error("Erreur de lecture / compression");
            }
        }
        e.target.value = "";
    };

    const removeImage = (idx) => {
        setImages((prev) => prev.filter((_, i) => i !== idx));
    };

    const addImageFromUrl = () => {
        const url = imageUrlInput.trim();
        if (!url) return;
        if (!/^https?:\/\/.+/i.test(url) && !url.startsWith("data:image/")) {
            toast.error("Lien invalide (doit commencer par http(s)://)");
            return;
        }
        setImages((prev) => [...prev, { url, caption: "" }]);
        setImageUrlInput("");
    };

    const updateCaption = (idx, caption) => {
        setImages((prev) => prev.map((img, i) => (i === idx ? { ...img, caption } : img)));
    };

    const submit = async () => {
        if (!name.trim()) return toast.error("Nom requis");
        setLoading(true);
        try {
            const payload = { name, description, color, notes, images };
            if (editing) {
                await api.patch(`/organizations/${editing.id}`, payload);
                toast.success("Organisation mise à jour");
            } else {
                await api.post("/organizations", payload);
                toast.success("Organisation créée");
            }
            onOpenChange(false);
            onDone && onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
        setLoading(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="org-dialog" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">
                        {editing ? "Modifier l'organisation" : "Nouvelle organisation"}
                    </DialogTitle>
                    <DialogDescription>
                        Fiche d'un groupe avec description, images (entrepôts, planques…)
                        et notes.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Nom
                            </Label>
                            <Input
                                data-testid="org-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Raza Perdida MC"
                                className="mt-2 h-11"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Couleur
                            </Label>
                            <Select value={color} onValueChange={setColor}>
                                <SelectTrigger className="mt-2 h-11">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {COLORS.map((c) => (
                                        <SelectItem key={c} value={c}>
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className={`w-3 h-3 rounded-full ${CATEGORY_COLORS[c].split(" ")[0]}`}
                                                />
                                                {c}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Description
                        </Label>
                        <Input
                            data-testid="org-description-input"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Motorcycle Club basé à Los Santos"
                            className="mt-2 h-11"
                        />
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Images (positions, entrepôts…) — {images.length}
                        </Label>
                        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {images.map((img, idx) => (
                                <div
                                    key={idx}
                                    className="rounded-md overflow-hidden border border-slate-200 bg-white"
                                >
                                    <div className="relative group aspect-square">
                                        <img
                                            src={img.url}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeImage(idx)}
                                            data-testid={`org-image-remove-${idx}`}
                                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    <Input
                                        data-testid={`org-image-caption-${idx}`}
                                        value={img.caption || ""}
                                        onChange={(e) => updateCaption(idx, e.target.value)}
                                        placeholder="Note (ex: Entrepôt)"
                                        className="border-0 rounded-none h-9 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                                    />
                                </div>
                            ))}
                            <label
                                htmlFor="org-images-input"
                                className="aspect-square rounded-md border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-slate-50 text-slate-500"
                            >
                                <Upload className="w-5 h-5" />
                                <span className="text-xs">Ajouter</span>
                            </label>
                            <input
                                id="org-images-input"
                                data-testid="org-images-input"
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={onAddImages}
                                className="hidden"
                            />
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            JPG/PNG · compressée automatiquement (max 1200 px)
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                            <div className="relative flex-1">
                                <LinkIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <Input
                                    data-testid="org-image-url-input"
                                    value={imageUrlInput}
                                    onChange={(e) => setImageUrlInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            addImageFromUrl();
                                        }
                                    }}
                                    placeholder="Ou collez un lien d'image (https://…)"
                                    className="h-10 pl-9"
                                />
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={addImageFromUrl}
                                data-testid="org-image-url-add-btn"
                                className="h-10"
                            >
                                Ajouter
                            </Button>
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Notes
                        </Label>
                        <Textarea
                            data-testid="org-notes-input"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="mt-2"
                            placeholder="Alliances, territoires, historique…"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>
                        Annuler
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={loading}
                        data-testid="org-submit-btn"
                        className="bg-[#3D4E2E] hover:bg-[#2E3A22] text-white"
                    >
                        {loading ? "Enregistrement…" : editing ? "Enregistrer" : "Créer"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
